import React, { useState, useEffect } from "react";
import { authFetch } from "../api";
import Header from "../components/Header";
import NeighborhoodSelect from "../components/NeighborhoodSelect";
import CurrencyInput from "../components/CurrencyInput";

// 5 visual columns fed by 4 server "groups". In Review + Reviewed share one grouped query
// (split client-side); each column still shows its own count via status_counts. Retired is
// windowed server-side to the last 14 days.
const COLUMNS = [
  { key: "TO_REVIEW", label: "New", color: "#e3f2fd", group: "new", pager: "new" },
  { key: "IN_REVIEW", label: "In Review", color: "#fff3e0", group: "in_review", status: "IN_REVIEW", pager: "in_review" },
  { key: "REVIEWED", label: "Reviewed", color: "#e8f5e9", group: "in_review", status: "REVIEWED" },
  { key: "MIS", label: "MIS", color: "#fce4ec", group: "mis", pager: "mis" },
  { key: "RETIRED", label: "Retired (14d)", color: "#f5f5f5", group: "retired", pager: "retired" },
];
const GROUP_KEYS = ["new", "in_review", "mis", "retired"];
const PAGE_SIZE = 25;
const EMPTY_GROUP = { rows: [], total: 0, status_counts: {}, page: 0 };

export default function SaleReviewsPage() {
  const [groups, setGroups] = useState({ new: { ...EMPTY_GROUP }, in_review: { ...EMPTY_GROUP }, mis: { ...EMPTY_GROUP }, retired: { ...EMPTY_GROUP } });
  const [loading, setLoading] = useState(true);
  const [paging, setPaging] = useState(null); // group key currently paging (for button state)
  const [draggedItem, setDraggedItem] = useState(null);

  // Modal state for MIS/Retired transitions
  const [showModal, setShowModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [statusComment, setStatusComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Filter draft states (staged — only sent to the server on "Search")
  const [searchAddress, setSearchAddress] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [bedsMin, setBedsMin] = useState("");
  const [bedsMax, setBedsMax] = useState("");
  const [bathsMin, setBathsMin] = useState("");
  const [bathsMax, setBathsMax] = useState("");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [capRateMin, setCapRateMin] = useState("");
  const [capRateMax, setCapRateMax] = useState("");
  const [daysOnMarketMax, setDaysOnMarketMax] = useState("");
  const [propertyType, setPropertyType] = useState("all");
  const [carryingCostMax, setCarryingCostMax] = useState("");
  const [sortBy, setSortBy] = useState("date_desc");
  const [selectedNeighborhoods, setSelectedNeighborhoods] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [defaultNeighborhoods, setDefaultNeighborhoods] = useState([]);

  const [applied, setApplied] = useState(null); // snapshot of filters behind the current results
  const [neighborhoodsLoaded, setNeighborhoodsLoaded] = useState(false);

  useEffect(() => { loadNeighborhoods(); }, []);
  useEffect(() => {
    if (neighborhoodsLoaded) loadAll(collectFilters());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [neighborhoodsLoaded]);

  const loadNeighborhoods = async () => {
    try {
      const data = await authFetch("/sale-reviews/neighborhoods");
      setNeighborhoods(data.neighborhoods);
      let defaultIds = [];
      if (data.default_neighborhoods && data.default_neighborhoods.length > 0) {
        defaultIds = data.default_neighborhoods.map((item) => {
          if (typeof item === "number") return item;
          if (typeof item === "string" && /^\d+$/.test(item)) return parseInt(item, 10);
          const found = data.neighborhoods.find((n) => n.name === item);
          return found ? found.id : null;
        }).filter((id) => id !== null);
      }
      setDefaultNeighborhoods(defaultIds);
      if (defaultIds.length > 0) setSelectedNeighborhoods(defaultIds);
      setNeighborhoodsLoaded(true);
    } catch (error) {
      console.error("Error loading neighborhoods:", error);
      setNeighborhoodsLoaded(true);
    }
  };

  const collectFilters = () => ({
    search: searchAddress,
    neighborhood_ids: selectedNeighborhoods,
    property_type: propertyType,
    beds_min: bedsMin, beds_max: bedsMax,
    baths_min: bathsMin, baths_max: bathsMax,
    price_min: priceMin, price_max: priceMax,
    cap_rate_min: capRateMin, cap_rate_max: capRateMax,
    dom_max: daysOnMarketMax,
    carrying_max: carryingCostMax,
    date_from: dateFrom, date_to: dateTo,
    sort_by: sortBy,
  });

  const buildParams = (f, group, pageNum) => {
    const p = new URLSearchParams();
    const add = (k, v) => { if (v !== "" && v !== null && v !== undefined) p.append(k, v); };
    p.append("group", group);
    // Search and neighborhood are independent constraints (logical AND): if a hood is
    // checked, always send it so an address search stays scoped to the selected hoods.
    if (f.search) p.append("search", f.search);
    if (f.neighborhood_ids && f.neighborhood_ids.length > 0) p.append("neighborhood_ids", f.neighborhood_ids.join(","));
    if (f.property_type && f.property_type !== "all") add("property_type", f.property_type);
    add("beds_min", f.beds_min); add("beds_max", f.beds_max);
    add("baths_min", f.baths_min); add("baths_max", f.baths_max);
    add("price_min", f.price_min); add("price_max", f.price_max);
    add("cap_rate_min", f.cap_rate_min); add("cap_rate_max", f.cap_rate_max);
    add("dom_max", f.dom_max); add("carrying_max", f.carrying_max);
    add("date_from", f.date_from); add("date_to", f.date_to);
    p.append("sort_by", f.sort_by || "date_desc");
    p.append("limit", PAGE_SIZE);
    p.append("offset", pageNum * PAGE_SIZE);
    return p;
  };

  const fetchGroup = async (f, group, pageNum) => {
    try {
      const data = await authFetch(`/sale-reviews?${buildParams(f, group, pageNum).toString()}`);
      setGroups((g) => ({ ...g, [group]: {
        rows: data.reviews || [], total: data.total_count || 0,
        status_counts: data.status_counts || {}, page: pageNum,
      } }));
    } catch (e) {
      console.error("group fetch failed:", group, e);
    }
  };

  const loadAll = async (f) => {
    setLoading(true);
    try {
      await Promise.all(GROUP_KEYS.map((g) => fetchGroup(f, g, 0)));
      setApplied(f);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => loadAll(collectFilters());

  const pageGroup = async (group, delta) => {
    const next = Math.max(0, (groups[group].page || 0) + delta);
    setPaging(group);
    await fetchGroup(applied || collectFilters(), group, next);
    setPaging(null);
  };

  // After a status change, refetch every group at its current page so the card moves to the
  // right column and all counts update.
  const refreshAll = () => {
    const f = applied || collectFilters();
    GROUP_KEYS.forEach((g) => fetchGroup(f, g, groups[g].page || 0));
  };

  const handleClearFilters = () => {
    setSearchAddress(""); setDateFrom(""); setDateTo("");
    setBedsMin(""); setBedsMax(""); setBathsMin(""); setBathsMax("");
    setPriceMin(""); setPriceMax(""); setCapRateMin(""); setCapRateMax("");
    setDaysOnMarketMax(""); setCarryingCostMax(""); setPropertyType("all"); setSortBy("date_desc");
    setSelectedNeighborhoods(defaultNeighborhoods.length > 0 ? defaultNeighborhoods : []);
  };

  // ── Drag & drop status changes ──
  const handleDragStart = (e, review) => { setDraggedItem(review); e.dataTransfer.effectAllowed = "move"; };
  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.status === newStatus) { setDraggedItem(null); return; }
    if (newStatus === "MIS" || newStatus === "RETIRED") {
      setPendingStatus(newStatus);
      setShowModal(true);
      return; // keep draggedItem until modal resolves
    }
    await updateStatus(newStatus, `Status changed from ${draggedItem.status} to ${newStatus}`);
  };

  const updateStatus = async (newStatus, reason) => {
    if (!draggedItem) return;
    const target = draggedItem;
    try {
      await authFetch(`/sale-reviews/${target.id}/status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus, reason }),
      });
      refreshAll();
    } catch (error) {
      console.error("Error updating status:", error);
      alert("Failed to update status");
    } finally {
      setDraggedItem(null);
    }
  };

  const handleModalConfirm = async () => {
    if (!statusComment.trim()) { alert("Please provide a reason for this status change."); return; }
    setSubmitting(true);
    await updateStatus(pendingStatus, statusComment);
    setSubmitting(false);
    setShowModal(false); setStatusComment(""); setPendingStatus(null);
  };

  const handleModalCancel = () => {
    setShowModal(false); setStatusComment(""); setPendingStatus(null); setDraggedItem(null);
  };

  const rowsFor = (col) => col.status ? (groups[col.group].rows || []).filter((r) => r.status === col.status) : (groups[col.group].rows || []);
  const countFor = (col) => col.status ? (groups[col.group].status_counts[col.status] || 0) : (groups[col.group].total || 0);

  // ── Styles / helpers ──
  const pageStyle = { minHeight: "100vh", backgroundColor: "#f5f5f5" };
  const containerStyle = { padding: "20px", maxWidth: "1800px", margin: "0 auto" };
  const boardStyle = { display: "grid", gridTemplateColumns: `repeat(${COLUMNS.length}, 1fr)`, gap: "20px", marginTop: "20px" };
  const columnStyle = { backgroundColor: "#fff", borderRadius: "8px", padding: "15px", minHeight: "500px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)", display: "flex", flexDirection: "column" };
  const columnHeaderStyle = (color) => ({ fontSize: "18px", fontWeight: "600", marginBottom: "15px", padding: "10px", backgroundColor: color, borderRadius: "4px", display: "flex", justifyContent: "space-between", alignItems: "center" });
  const cardStyle = { backgroundColor: "#fff", border: "1px solid #e0e0e0", borderRadius: "6px", padding: "12px", marginBottom: "10px", cursor: "grab", transition: "all 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" };
  const cardHoverStyle = { transform: "translateY(-2px)", boxShadow: "0 4px 8px rgba(0,0,0,0.15)" };
  const addressStyle = { fontSize: "14px", fontWeight: "600", marginBottom: "8px", color: "#333" };
  const detailsStyle = { fontSize: "12px", color: "#666", marginBottom: "4px" };
  const badgeStyle = { backgroundColor: "#e3f2fd", color: "#1976d2", padding: "2px 8px", borderRadius: "12px", fontSize: "11px", fontWeight: "500" };
  const inputStyle = { width: "100%", padding: "6px 8px", border: "1px solid #ced4da", borderRadius: "4px", fontSize: "13px" };
  const labelStyle = { display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "4px", color: "#495057" };

  const getPriceStyle = (priceChange) => {
    let color = "#000";
    if (priceChange && priceChange < 0) color = "#2e7d32";
    else if (priceChange && priceChange > 0) color = "#d32f2f";
    return { fontSize: "16px", fontWeight: "bold", color, marginTop: "8px" };
  };
  const formatPriceChange = (change, pct) => {
    if (!change || change === 0) return null;
    const sign = change > 0 ? "+" : "-";
    const absChange = Math.abs(change);
    const formattedChange = absChange >= 1000 ? `${sign}$${(absChange / 1000).toFixed(0)}K` : `${sign}$${absChange.toLocaleString()}`;
    const formattedPct = pct ? ` (${sign}${Math.abs(pct).toFixed(1)}%)` : "";
    return `${formattedChange}${formattedPct}`;
  };

  if (loading) {
    return (
      <div style={pageStyle}>
        <Header showLogout={true} />
        <div style={containerStyle}>
          <div style={{ textAlign: "center", marginTop: "50px" }}>Loading reviews...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <Header showLogout={true} />
      <div style={containerStyle}>
        {/* Filter Panel */}
        <div style={{ padding: "16px", backgroundColor: "#fff", borderRadius: "8px", marginBottom: "20px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "12px", marginBottom: "12px" }}>
            <NeighborhoodSelect
              neighborhoods={neighborhoods}
              selected={selectedNeighborhoods}
              onChange={setSelectedNeighborhoods}
              defaults={defaultNeighborhoods}
              placeholder="All Neighborhoods"
              gridColumn="span 2"
            />

            <div>
              <label style={labelStyle}>Address (use # for unit)</label>
              <input type="text" placeholder="Search by address or listing ID..." value={searchAddress}
                onChange={(e) => setSearchAddress(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleSearch(); }} style={inputStyle} />
            </div>

            <div>
              <label style={labelStyle}>Property Type</label>
              <select value={propertyType} onChange={(e) => setPropertyType(e.target.value)} style={{ ...inputStyle, backgroundColor: "white" }}>
                <option value="all">All Properties</option>
                <option value="condo">Condo</option>
                <option value="coop">Co-op</option>
              </select>
            </div>

            <div>
              <label style={labelStyle}>Bedrooms</label>
              <div style={{ display: "flex", gap: "8px" }}>
                <input type="number" placeholder="Min" value={bedsMin} onChange={(e) => setBedsMin(e.target.value)} style={inputStyle} />
                <input type="number" placeholder="Max" value={bedsMax} onChange={(e) => setBedsMax(e.target.value)} style={inputStyle} />
              </div>
            </div>

            <div>
              <label style={labelStyle}>Bathrooms</label>
              <div style={{ display: "flex", gap: "8px" }}>
                <input type="number" step="0.5" placeholder="Min" value={bathsMin} onChange={(e) => setBathsMin(e.target.value)} style={inputStyle} />
                <input type="number" step="0.5" placeholder="Max" value={bathsMax} onChange={(e) => setBathsMax(e.target.value)} style={inputStyle} />
              </div>
            </div>

            <div>
              <label style={labelStyle}>Price ($)</label>
              <div style={{ display: "flex", gap: "8px" }}>
                <CurrencyInput placeholder="Min" value={priceMin} onChange={setPriceMin} style={inputStyle} />
                <CurrencyInput placeholder="Max" value={priceMax} onChange={setPriceMax} style={inputStyle} />
              </div>
            </div>

            <div>
              <label style={labelStyle}>AI Cap Rate (%)</label>
              <div style={{ display: "flex", gap: "8px" }}>
                <select value={capRateMin} onChange={(e) => setCapRateMin(e.target.value)} style={{ ...inputStyle, backgroundColor: "white" }}>
                  <option value="">From</option>
                  {[2, 3, 4, 5, 6, 7, 8, 9, 10].map((v) => <option key={v} value={v}>{v}%</option>)}
                </select>
                <select value={capRateMax} onChange={(e) => setCapRateMax(e.target.value)} style={{ ...inputStyle, backgroundColor: "white" }}>
                  <option value="">To</option>
                  {[2, 3, 4, 5, 6, 7, 8, 9, 10].map((v) => <option key={v} value={v}>{v}%</option>)}
                </select>
              </div>
            </div>

            <div>
              <label style={labelStyle}>Days on Market (max)</label>
              <input type="number" placeholder="Max days" min="0" value={daysOnMarketMax} onChange={(e) => setDaysOnMarketMax(e.target.value)} style={inputStyle} />
            </div>

            <div>
              <label style={labelStyle}>HOA + Tax (max)</label>
              <CurrencyInput placeholder="e.g. 1000" value={carryingCostMax} onChange={setCarryingCostMax} style={inputStyle} />
            </div>

            <div>
              <label style={labelStyle}>Date Listed</label>
              <div style={{ display: "flex", gap: "8px" }}>
                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={inputStyle} />
                <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={inputStyle} />
              </div>
            </div>

            <div>
              <label style={labelStyle}>Sort By</label>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} style={{ ...inputStyle, backgroundColor: "white" }}>
                <option value="date_desc">Newest First</option>
                <option value="date_asc">Oldest First</option>
                <option value="cap_desc">Cap Rate (High to Low)</option>
                <option value="cap_asc">Cap Rate (Low to High)</option>
                <option value="price_asc">Price (Low to High)</option>
                <option value="price_desc">Price (High to Low)</option>
                <option value="dom_asc">DOM (Low to High)</option>
                <option value="dom_desc">DOM (High to Low)</option>
              </select>
            </div>

            <div style={{ display: "flex", alignItems: "flex-end", gap: "8px", gridColumn: "span 3" }}>
              <button onClick={handleSearch} disabled={loading}
                style={{ padding: "8px 16px", backgroundColor: "#000", color: "white", border: "none", borderRadius: "4px", fontSize: "14px", fontWeight: "500", cursor: loading ? "wait" : "pointer" }}>
                {loading ? "Loading..." : "Search"}
              </button>
              <button onClick={handleClearFilters}
                style={{ padding: "8px 16px", backgroundColor: "#6c757d", color: "white", border: "none", borderRadius: "4px", fontSize: "14px", fontWeight: "500", cursor: "pointer" }}>
                Reset
              </button>
            </div>
          </div>
        </div>

        <div style={boardStyle}>
          {COLUMNS.map((col) => {
            const rows = rowsFor(col);
            const count = countFor(col);
            const grp = col.pager ? groups[col.pager] : null;
            const totalPages = grp ? Math.max(1, Math.ceil(grp.total / PAGE_SIZE)) : 1;
            return (
              <div key={col.key} style={columnStyle} onDragOver={handleDragOver} onDrop={(e) => handleDrop(e, col.key)}>
                <div style={columnHeaderStyle(col.color)}>
                  <span>{col.label}</span>
                  <span style={badgeStyle}>{count.toLocaleString()}</span>
                </div>

                <div style={{ flex: 1 }}>
                  {rows.map((review) => (
                    <div key={review.id} style={cardStyle} draggable
                      onDragStart={(e) => handleDragStart(e, review)}
                      onMouseEnter={(e) => Object.assign(e.currentTarget.style, cardHoverStyle)}
                      onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.1)"; }}>
                      <div style={addressStyle}>
                        {review.property.address}{review.property.unit_number && ` #${review.property.unit_number}`}
                        <span onClick={(e) => {
                          e.stopPropagation();
                          const addr = (review.property.address || "").replace(/\s+/g, " ").trim();
                          const unit = (review.property.unit_number || "").trim();
                          navigator.clipboard?.writeText(`${addr}${unit ? ` #${unit}` : ""}`);
                        }} title="Copy address" style={{ cursor: "pointer", marginLeft: 6, fontSize: 12, opacity: 0.55 }}>📋</span>
                        {review.verified && (
                          <span title="Data verified" style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: "#2e7d32", background: "#e8f5e9", border: "1px solid #a5d6a7", borderRadius: 4, padding: "1px 5px", verticalAlign: "middle", whiteSpace: "nowrap" }}>✓ Verified</span>
                        )}
                      </div>
                      {review.property.property_type && (
                        <span style={{ display: "inline-block", fontSize: "11px", fontWeight: 600, color: "#555", border: "1px solid #ddd", borderRadius: "4px", padding: "1px 6px", marginBottom: "4px" }}>
                          {["COOP", "CONDP"].includes((review.property.property_type || "").toUpperCase()) ? "Co-op"
                            : (review.property.property_type || "").toUpperCase() === "CONDO" ? "Condo" : review.property.property_type}
                        </span>
                      )}
                      <div style={detailsStyle}>{review.property.neighborhood}, {review.property.borough}</div>
                      {review.property.listed_at && (
                        <div style={detailsStyle}>Listed: {new Date(review.property.listed_at).toLocaleDateString()}</div>
                      )}
                      <div style={detailsStyle}>
                        {review.property.bedrooms !== null ? review.property.bedrooms : "?"} bed •{" "}
                        {review.property.bathrooms || "?"} bath • {review.property.sqft || "?"} sqft
                      </div>
                      {review.property.price && (
                        <div style={getPriceStyle(review.property.price_change)}>
                          ${review.property.price.toLocaleString()}
                          {formatPriceChange(review.property.price_change, review.property.price_change_pct) && (
                            <span style={{ fontSize: "12px", fontWeight: "500", marginLeft: "6px" }}>
                              {formatPriceChange(review.property.price_change, review.property.price_change_pct)}
                            </span>
                          )}
                        </div>
                      )}
                      {(review.property.ai_cap_rate != null || review.property.cap_rate != null || review.property.neighborhood_avg_cap_rate != null) && (
                        <div style={{ fontSize: "14px", fontWeight: "600", marginTop: "4px" }}>
                          {review.property.ai_cap_rate != null && (
                            <span style={{ color: "#1976d2" }}>AI Cap: {review.property.ai_cap_rate.toFixed(2)}%</span>
                          )}
                          {review.property.cap_rate != null && (
                            <span style={{ color: "#607d8b", marginLeft: review.property.ai_cap_rate != null ? "10px" : 0 }}>
                              ML Cap: {review.property.cap_rate.toFixed(2)}%
                            </span>
                          )}
                          {/* Neighborhood benchmark shows regardless of whether this listing
                              has its own prediction — it's a property of the hood, not the listing. */}
                          {review.property.neighborhood_avg_cap_rate != null && (
                            <span style={{ fontSize: "12px", fontWeight: "400", color: "#666", marginLeft: (review.property.ai_cap_rate != null || review.property.cap_rate != null) ? "8px" : 0 }}>
                              (Nbhd avg: {review.property.neighborhood_avg_cap_rate.toFixed(2)}%)
                            </span>
                          )}
                        </div>
                      )}
                      {review.property.below_building_sale && (
                        <div style={{ fontSize: "12px", fontWeight: "700", color: "#e65100", backgroundColor: "#fff3e0", padding: "3px 8px", borderRadius: "4px", marginTop: "6px", border: "1px solid #ffcc80", display: "inline-block" }}
                          title={`Match: ${review.property.below_building_sale.match_type === "same_line" ? "Same line" : "Same bed/bath/sqft"} | Sold: $${review.property.below_building_sale.sold_price?.toLocaleString()} (${review.property.below_building_sale.sold_date?.slice(0, 7) || "?"})`}>
                          {review.property.below_building_sale.pct_below}% below sold #{review.property.below_building_sale.sold_unit}
                        </div>
                      )}
                      {review.status_reason && (
                        <div style={{ fontSize: "12px", color: "#555", marginTop: "6px", padding: "6px 8px", backgroundColor: "#f5f5f5", borderRadius: "4px", borderLeft: "3px solid #1976d2", fontStyle: "italic" }}>
                          {review.status_reason}
                        </div>
                      )}
                      <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                        <button style={{ padding: "4px 8px", fontSize: "11px", backgroundColor: "#1976d2", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer" }}
                          onClick={(e) => { e.stopPropagation(); window.open(`/predict/${review.sale_listing_id}`, "_blank"); }}>View Analysis</button>
                        <button style={{ padding: "4px 8px", fontSize: "11px", backgroundColor: "#7b1fa2", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer" }}
                          onClick={(e) => { e.stopPropagation(); window.open(`/comps/${review.sale_listing_id}`, "_blank"); }}>Comps</button>
                        {review.property.listing_url && (
                          <button style={{ padding: "4px 8px", fontSize: "11px", backgroundColor: "#2e7d32", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer" }}
                            onClick={(e) => { e.stopPropagation(); window.open(review.property.listing_url, "_blank"); }}>Web Listing</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Per-column Prev/Next (only the group's primary column has the pager) */}
                {col.pager && grp.total > PAGE_SIZE && (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "6px", marginTop: "10px", paddingTop: "10px", borderTop: "1px solid #eee" }}>
                    <button disabled={grp.page <= 0 || paging === col.pager} onClick={() => pageGroup(col.pager, -1)}
                      style={pagerBtn(grp.page <= 0 || paging === col.pager)}>‹</button>
                    <span style={{ fontSize: "11px", color: "#666" }}>{grp.page + 1}/{totalPages}</span>
                    <button disabled={grp.page >= totalPages - 1 || paging === col.pager} onClick={() => pageGroup(col.pager, 1)}
                      style={pagerBtn(grp.page >= totalPages - 1 || paging === col.pager)}>›</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Modal for MIS/Retired comment */}
      {showModal && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10000 }}>
          <div style={{ backgroundColor: "#fff", borderRadius: "8px", padding: "24px", maxWidth: "500px", width: "90%", boxShadow: "0 4px 20px rgba(0,0,0,0.3)" }}>
            <h3 style={{ margin: "0 0 16px 0", fontSize: "18px", color: "#333" }}>
              {pendingStatus === "MIS" ? "Mark as MIS" : "Mark as Retired"}
            </h3>
            <p style={{ margin: "0 0 16px 0", fontSize: "14px", color: "#666" }}>
              Please provide a reason for this status change (required):
            </p>
            <textarea value={statusComment} onChange={(e) => setStatusComment(e.target.value)} placeholder="Enter reason..." autoFocus
              style={{ width: "100%", minHeight: "100px", padding: "12px", fontSize: "14px", border: "1px solid #ddd", borderRadius: "4px", fontFamily: "inherit", resize: "vertical", boxSizing: "border-box" }} />
            <div style={{ display: "flex", gap: "12px", marginTop: "20px", justifyContent: "flex-end" }}>
              <button onClick={handleModalCancel} disabled={submitting}
                style={{ padding: "10px 20px", fontSize: "14px", fontWeight: "600", backgroundColor: "#f5f5f5", color: "#333", border: "1px solid #ddd", borderRadius: "6px", cursor: submitting ? "not-allowed" : "pointer", opacity: submitting ? 0.6 : 1 }}>
                Cancel
              </button>
              <button onClick={handleModalConfirm} disabled={!statusComment.trim() || submitting}
                style={{ padding: "10px 20px", fontSize: "14px", fontWeight: "600", backgroundColor: pendingStatus === "MIS" ? "#4caf50" : "#ff9800", color: "#fff", border: "none", borderRadius: "6px", cursor: (!statusComment.trim() || submitting) ? "not-allowed" : "pointer", opacity: (!statusComment.trim() || submitting) ? 0.5 : 1 }}>
                {submitting ? "Updating..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const pagerBtn = (disabled) => ({
  padding: "3px 12px", fontSize: "14px", border: "1px solid #ccc", borderRadius: "4px",
  background: disabled ? "#f5f5f5" : "#fff", color: disabled ? "#bbb" : "#333",
  cursor: disabled ? "not-allowed" : "pointer",
});
